import { intentionalMemoryDigestPacketSchema, } from "./memory-packet.js";
import { memoryApplicabilitySchema, } from "./memories.js";
const APPLICABILITY_DIMENSIONS = [
    "databaseIds",
    "datasetIds",
    "entityTypes",
    "entityIds",
    "schemaFingerprints",
    "metrics",
    "operations",
];
function reason(code, message) {
    return { code, message };
}
function scopeMatches(packet, item) {
    switch (item.scopeType) {
        case "user":
            return item.scopeId === packet.scope.actorId;
        case "channel":
            return packet.scope.channelId !== null && item.scopeId === packet.scope.channelId;
        case "organisation":
            return packet.scope.organisationId !== null &&
                item.scopeId === packet.scope.organisationId;
    }
}
function validityReason(item, now) {
    if (item.validFrom === null) {
        return reason("missing_validity", "The memory has no authoritative validity start.");
    }
    if (item.validFrom > now) {
        return reason("memory_not_yet_valid", "The memory validity window has not started.");
    }
    if (item.validTo !== null && item.validTo <= now) {
        return reason("memory_expired", "The memory validity window has ended.");
    }
    if (item.observedAt > item.ingestedAt || item.observedAt > now || item.ingestedAt > now) {
        return reason("invalid_temporal_metadata", "The memory observation and ingestion timestamps are not trustworthy for this turn.");
    }
    return null;
}
function applicabilityReason(item, resolved) {
    const constrained = APPLICABILITY_DIMENSIONS.filter((dimension) => (item.applicability[dimension]?.length ?? 0) > 0);
    if (constrained.length === 0)
        return null;
    if ((item.applicability.schemaFingerprints?.length ?? 0) > 0 &&
        (resolved?.schemaFingerprints?.length ?? 0) === 0) {
        return reason("unresolved_schema_fingerprint", "The current schema fingerprint is unavailable, so this memory cannot be applied safely.");
    }
    for (const dimension of constrained) {
        const actual = resolved?.[dimension];
        if (!actual || actual.length === 0) {
            return reason("unresolved_applicability", `The current ${dimension} applicability dimension is unresolved.`);
        }
        const actualValues = new Set(actual);
        if (!item.applicability[dimension].some((value) => actualValues.has(value))) {
            return reason("applicability_mismatch", `The memory does not match the current ${dimension} applicability dimension.`);
        }
    }
    return null;
}
function conflictKey(item) {
    return `${item.scopeType}\u0000${item.scopeId}\u0000${item.memoryKey}`;
}
function conflictingRefs(packet, now) {
    const candidates = packet.items.filter((item) => item.status === "active" && scopeMatches(packet, item) &&
        validityReason(item, now) === null);
    const byKey = new Map();
    for (const item of candidates) {
        const key = conflictKey(item);
        byKey.set(key, [...(byKey.get(key) ?? []), item]);
    }
    const conflicts = new Set();
    for (const items of byKey.values()) {
        if (new Set(items.map((item) => item.content)).size <= 1)
            continue;
        for (const item of items)
            conflicts.add(item.versionId);
    }
    return conflicts;
}
function itemAdmissionReason(input) {
    const { item } = input;
    if (!scopeMatches(input.packet, item)) {
        return reason("scope_mismatch", "The memory scope does not match the authoritative turn scope.");
    }
    if (item.status !== "active") {
        return reason("inactive_memory", "Only active intentional-memory versions can be admitted.");
    }
    const temporal = validityReason(item, input.now);
    if (temporal)
        return temporal;
    if (item.verification.status !== "unverified" &&
        item.verification.verifiedAt > input.now) {
        return reason("invalid_verification_metadata", "The memory verification timestamp is in the future for this turn.");
    }
    if (input.conflicts.has(item.versionId)) {
        return reason("conflicting_active_memories", "Active memories with the same scoped key contain conflicting content.");
    }
    if (item.verification.status === "rejected") {
        return reason("rejected_verification", "A reviewer rejected this memory version.");
    }
    if (item.authority !== "user" && item.authority !== "reviewer") {
        return reason("low_authority", "Only user-authored or reviewer-authored memory is trusted for direct context admission.");
    }
    if (item.memoryType === "procedure" && item.verification.status !== "verified") {
        return reason("unverified_procedure", "Procedural memory requires explicit verification before it can influence execution.");
    }
    return input.purpose === "lifecycle_target"
        ? null
        : applicabilityReason(item, input.resolvedApplicability);
}
/** Deterministic, fail-closed admission shared by every intentional-memory path. */
export function admitIntentionalMemoryDigestPacket(input) {
    const packet = intentionalMemoryDigestPacketSchema.parse(input.packet);
    if (!Number.isSafeInteger(input.now) || input.now < 0) {
        throw new Error("Intentional-memory admission requires a valid current timestamp");
    }
    const resolvedApplicability = input.resolvedApplicability === undefined
        ? undefined
        : memoryApplicabilitySchema.parse(input.resolvedApplicability);
    const purpose = input.purpose ?? "application";
    const conflicts = purpose === "lifecycle_target"
        ? new Set()
        : conflictingRefs(packet, input.now);
    const admittedItems = [];
    const rejectedByReason = new Map();
    for (const item of packet.items) {
        const rejection = itemAdmissionReason({
            packet,
            item,
            now: input.now,
            resolvedApplicability,
            purpose,
            conflicts,
        });
        if (!rejection) {
            admittedItems.push(item);
            continue;
        }
        const rejectionKey = `${rejection.code}\u0000${rejection.message}`;
        const grouped = rejectedByReason.get(rejectionKey);
        if (grouped)
            grouped.items.push(item);
        else
            rejectedByReason.set(rejectionKey, { items: [item], reason: rejection });
    }
    return {
        packet,
        admittedItems,
        rejections: [...rejectedByReason.values()],
    };
}
